import { FetchResult, useMutation } from "@apollo/client";
import { FormikHelpers, Formik, Form } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import { useTheme } from "@material-ui/core";

import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikSwitchField from "src/components/form/FormikSwitchField";
import FormikTextField from "src/components/form/FormikTextField";
import SubmitButton from "src/components/form/SubmitButton";
import { EDIT_CAMPAIGN_ALGORITHM } from "src/graphql/campaignAlgorithm";
import {
  campaignEditAlgorithm,
  campaignEditAlgorithmVariables
} from "src/graphql/generated/campaignEditAlgorithm";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";

type Values = {
  useAlternateDropRate: boolean;
  maxRatio: string;
};

export function CampaignAlgorithmForm({
  campaign
}: {
  campaign: getCampaign_campaign;
}): JSX.Element {
  const [campaignEditAlgorithm] = useMutation<
    campaignEditAlgorithm,
    campaignEditAlgorithmVariables
  >(EDIT_CAMPAIGN_ALGORITHM);

  const onSubmit = async ({
    useAlternateDropRate,
    maxRatio
  }: {
    useAlternateDropRate: boolean;
    maxRatio: string;
  }): Promise<FetchResult<any>> =>
    campaignEditAlgorithm({
      variables: {
        campaignId: campaign.id,
        useAlternateDropRate,
        maxRatio: _.parseInt(maxRatio)
      }
    });

  const snackbar = useSnackbar();
  const theme = useTheme();

  const schema = yup.object().shape({
    useAlternateDropRate: yup.boolean().required("Required"),
    maxRatio: yup
      .number()
      .max(30, "No more juice for you Nate!")
      .min(1)
      .required("Required")
  });

  const handleSubmit = async (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => {
    const { errors } = await onSubmit(values);
    if (!errors) {
      snackbar.enqueueSnackbar("Campaign algorithm settings updated.", {
        variant: "info"
      });
      helpers.resetForm({
        values: {
          useAlternateDropRate: values.useAlternateDropRate,
          maxRatio: values.maxRatio
        }
      });
    } else {
      snackbar.enqueueSnackbar(errors[0].message, { variant: "error" });
      helpers.resetForm();
    }
  };

  return (
    <Formik
      initialValues={{
        useAlternateDropRate: campaign.useAlternateDropRate,
        maxRatio: campaign.maxRatio.toString()
      }}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ isSubmitting, dirty }) => (
        <Form>
          <FormikErrorBanner />

          <FormikSwitchField
            name="useAlternateDropRate"
            label="Use alternate drop rate"
            helperText="Enabling this uses a more conservative algorithm that minimizes drops"
          />

          <FormikTextField
            name="maxRatio"
            label="Maximum dialer ratio (only go above 15 with blessing from devs)"
            fullWidth
            style={{ marginTop: theme.spacing(4) }}
          />

          <SubmitButton
            type="submit"
            disabled={!dirty}
            isSubmitting={isSubmitting}
            submitText="Update"
            loadingText="updating..."
          />
        </Form>
      )}
    </Formik>
  );
}
